/**
 * Rebate Panel Component (Campaign Variant)
 * Displays rebate info in panel format within the campaign system.
 * Follows the same pattern as PrizePopPanel/TournamentPanel.
 */

import { type FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { SECTION_NAMES } from '@repo/common';
import {
  REBATE_CAMPAIGN_TYPE,
  useAutoJoin,
  useCampaignDataAttributes,
  useSelectedCampaignId,
} from 'features/campaigns';
import JoinInButton from '../../../shared/JoinIn/JoinInButton';
import { useCampaignCardScale } from 'features/campaigns/_exports';
import { useTrueMobile } from 'shared/hooks/useTrueMobile';
import { setSelectedSection, setViewMode } from 'store/app/appStore';
import { SINGLE_VIEW_MODE } from '../../../../../../constants';
import { useCampaignStatus } from '../../../../hooks/data/campaignStatus/useCampaignStatus';
import type { CampaignPanelInfo } from '../../../MultiViewCampaignSection/types';
import PeriodDisplay from '../../../shared/PeriodDisplay/PeriodDisplay';
import SpikedTextFrame from '../../../shared/SpikedTextFrame/SpikedTextFrame';
import StartsIn from '../../../shared/StartsIn/StartsIn';
import { RebateSubtitleCampaign } from '../shared';

import styles from './RebatePanel.module.css';

interface RebatePanelProps {
  panelInfo: CampaignPanelInfo;
}

const TITLE_LENGTH_LIMIT = 22;

const RebatePanel: FC<RebatePanelProps> = ({ panelInfo }) => {
  const { t } = useTranslation();
  const { backgroundOverlayImage, headerTitle, joined, startDate = '', endDate = '', isSingleItem = false } = panelInfo;
  const dataAttributes = useCampaignDataAttributes({
    campaignType: REBATE_CAMPAIGN_TYPE,
    additionalProps: { 'data-variant': 'panel' }
  });

  const selectedRebateId = useSelectedCampaignId(REBATE_CAMPAIGN_TYPE) as string;

  const autoJoin = useAutoJoin(REBATE_CAMPAIGN_TYPE);
  const { isFinished } = useCampaignStatus(selectedRebateId);
  const showJoinButton = !!selectedRebateId && !autoJoin && isSingleItem && (!isFinished || joined);

  const { titleRef, scale } = useCampaignCardScale(headerTitle, {
    recalculateKey: isSingleItem,
  });

  const isMobile = useTrueMobile();
  const showSpikes = isMobile || headerTitle.length <= TITLE_LENGTH_LIMIT;

  const handleClick = useCallback(() => {
    setSelectedSection(SECTION_NAMES.REBATE);
    setViewMode(SINGLE_VIEW_MODE);
  }, []);

  return (
    <div
      className={styles.panelContainer}
      style={{ backgroundImage: `url(${backgroundOverlayImage})` }}
      {...dataAttributes}
    >
      {showJoinButton && <JoinInButton campaignId={selectedRebateId} variant="panel" />}
      <div className={styles.centerInfoWrapper} {...dataAttributes}>
        <div className={styles.mainTitleWrapper}>
          <SpikedTextFrame show={showSpikes}>
            <span
              className={styles.campaignInfoTitle}
              ref={titleRef as React.RefObject<HTMLSpanElement>}
              style={{
                transform: scale < 1 ? `scale(${scale})` : undefined,
              }}
            >
              {headerTitle}
            </span>
          </SpikedTextFrame>
        </div>
        <RebateSubtitleCampaign
          content={t('rebate.slogan', 'Where every bet pays back')}
          variant="panel"
          isSingleItem={isSingleItem}
        />
      </div>
          
      <div
        className={styles.periodWrapper}
        onClick={handleClick}
        {...dataAttributes}
      >
        {isSingleItem && !isFinished
          ? <StartsIn />
          : <>
            <div className={styles.separator} />
            <PeriodDisplay startDate={startDate} endDate={endDate} isFinished={isFinished && isSingleItem} />
          </>
        }
      </div>
    </div>
  );
};

export default RebatePanel;
